import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  InformationCircleIcon,
  ClockIcon,
  PhotoIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  NewspaperIcon,
} from '@heroicons/react/24/outline';
import HomeContentContainer from '../components/HomeContentContainer';
import AboutContentContainer from '../components/AboutContentContainer';
import HistoryContentContainer from '../components/HistoryContentContainer';
import RegistrationContentContainer from '../components/RegistrationContentContainer';
import HeroSliderContentContainer from '../components/HeroSliderContentContainer';
import GalleryContentContainer from '../components/GalleryContentContainer';
import LogoManagement from '../components/LogoManagement';
import SupportContentContainer from '../components/SupportContentContainer';
import PressContentContainer from '../components/PressContentContainer';

interface ContentsProps {
  selectedPage?: string;
  handleBackToOverview?: () => void;
}

export default function Contents({ selectedPage = '', handleBackToOverview }: ContentsProps) {
  const [activePage, setActivePage] = useState(selectedPage || '');

  const handlePageSelect = (page: string) => {
    setActivePage(page);
  };

  const handleBack = () => {
    setActivePage('');
    if (handleBackToOverview) {
      handleBackToOverview();
    }
  };

  const renderPageContent = () => {
    switch (activePage) {
      case 'home':
        return <HomeContentContainer />;
      case 'about':
        return <AboutContentContainer />;
      case 'hero-slider':
        return <HeroSliderContentContainer />;
      case 'registration':
        return <RegistrationContentContainer />;
      case 'history':
        return <HistoryContentContainer />;
      case 'gallery':
        return <GalleryContentContainer />;
      case 'logos':
        return <LogoManagement />;
      case 'support':
        return <SupportContentContainer />;
      case 'press':
        return <PressContentContainer />;
      default:
        return renderOverview();
    }
  };

  const renderOverview = () => {
    const contentPages = [
      { id: 'home', name: 'Home Page', icon: HomeIcon, description: 'Manage home page content sections' },
      { id: 'about', name: 'About Page', icon: InformationCircleIcon, description: 'Manage about page content' },
      { id: 'hero-slider', name: 'Hero Slider', icon: HomeIcon, description: 'Manage homepage hero slider' },
      { id: 'registration', name: 'Registration Page', icon: DocumentTextIcon, description: 'Manage registration page content' },
      { id: 'history', name: 'History Page', icon: ClockIcon, description: 'Manage history page content' },
      { id: 'gallery', name: 'Gallery Page', icon: PhotoIcon, description: 'Manage photo galleries' },
      { id: 'logos', name: 'Logo Management', icon: PhotoIcon, description: 'Manage website logos' },
      { id: 'support', name: 'Support Page', icon: QuestionMarkCircleIcon, description: 'Manage FAQs and contact support content' },
      { id: 'press', name: 'Press / Media', icon: NewspaperIcon, description: 'Manage media guidelines for press registration' }
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {contentPages.map((page) => (
          <motion.div
            key={page.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => handlePageSelect(page.id)}
          >
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="bg-golf-orange/10 p-3 rounded-xl">
                  <page.icon className="w-6 h-6 text-golf-orange" />
                </div>
                <h3 className="text-lg font-semibold text-golf-gray">{page.name}</h3>
              </div>
              <p className="text-gray-600">{page.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6">
      {activePage ? (
        <>
          <div className="flex items-center mb-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-golf-orange hover:text-golf-green transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back to Content Overview</span>
            </button>
          </div>
          {renderPageContent()}
        </>
      ) : (
        <>
          <h2 className="text-2xl font-bold text-golf-gray mb-6">Content Management</h2>
          {renderPageContent()}
        </>
      )}
    </div>
  );
}